import axios from "axios";
import { useQuery } from "react-query";

export function useTagDetails(token) {
    return useQuery("tagDetails", async () => {
        const response = await axios.get("/api/tag-details", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    }, {
        enabled: !!token,
        staleTime: 10 * 60 * 1000,
        cacheTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

export function useProjectDetails(token) {
    return useQuery("projectDetails", async () => {
        const response = await axios.get("/api/project-details", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    });
}

export function useClientProjectDetails(token) {
    return useQuery("clientProjectDetails", async () => {
        const response = await axios.get("/api/client-project-details", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    });
}

export function useGroupedTimerData(token, selectedUser, page = 1, limit = 10) {
    return useQuery(
        ["groupTimerData", selectedUser, page, limit],
        async () => {
            const response = await axios.get("/api/time-track/grouped/get-timer", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    tz: global.config.getTimeZone(),
                    userId: selectedUser,
                    page: page,
                    limit: limit
                },
            });

            // Return the data in the expected format
            // If response has pagination structure, return data array
            // Otherwise, return as-is for backward compatibility
            if (response.data && response.data.data && Array.isArray(response.data.data)) {
                return response.data;
            }
            // Backward compatibility: if it's just an array, wrap it
            if (Array.isArray(response.data)) {
                return {
                    data: response.data,
                    pagination: {
                        page: 1,
                        limit: response.data.length,
                        total: response.data.length,
                        totalPages: 1
                    }
                };
            }
            return response.data;
        },
        {
            enabled: !!selectedUser, // Only fetch if selectedUser is available
            keepPreviousData: true, // Keep previous data while fetching new page
        }
    );
}

export function useTimerData(token, selectedUser, page = 1, limit = 10) {
    return useQuery(
        ["timerData", selectedUser, page, limit],
        async () => {
            const response = await axios.get("/api/time-track/get-timer", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    tz: global.config.getTimeZone(),
                    userId: selectedUser,
                    page: page,
                    limit: limit
                },
            });

            // Handle paginated response structure
            let timerData = {};
            let pagination = { page: 1, limit: 0, total: 0, totalPages: 1 };

            if (response.data && response.data.data && typeof response.data.data === 'object') {
                // New paginated structure
                timerData = response.data.data;
                pagination = response.data.pagination || pagination;
            } else if (typeof response.data === 'object' && !Array.isArray(response.data)) {
                // Backward compatibility: old structure (object with date keys)
                timerData = response.data;
                const totalDays = Object.keys(timerData).length;
                pagination = {
                    page: 1,
                    limit: totalDays,
                    total: totalDays,
                    totalPages: 1
                };
            }

            // Convert to array format [date, entries] for compatibility
            var result = Object.keys(timerData).map((key) => [
                key,
                timerData[key],
            ]);

            return {
                data: result,
                pagination: pagination
            };
        },
        {
            enabled: !!selectedUser, // Only fetch if selectedUser is available
            keepPreviousData: true, // Keep previous data while fetching new page
        }
    );
}

export function useTimeZone(userId, token) {
    return useQuery(['timeZone', userId], async () => {
        const response = await axios.get(`/api/get/user-settings/${userId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    }, {
        onError: (error) => {
            global.config.slackMessage(error.toString());
            console.error(error);
        },
    });
}

export function useUserData(userId, token) {
    return useQuery(['userData', userId], async () => {
        const response = await axios.get(`/api/user/view/${userId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    }, {
        onError: (error) => {
            global.config.slackMessage(error.toString());
            console.error(error);
        },
    });
}